// Global function to fix single-variant product buttons immediately
// This runs as soon as possible, even before DOM is ready
(function() {
  function fixSingleVariantButtons() {
    // Check all product forms on the page
    document.querySelectorAll('product-form').forEach((productForm) => {
      // Check if marked as single-variant
      const isSingleVariant = productForm.hasAttribute('data-single-variant') || 
                             productForm.dataset.singleVariant === 'true';
      
      const productInfo = productForm.closest('product-info');
      if (!productInfo) return;
      
      const variantSelects = productInfo.querySelector('product-variant-selects');
      const variantInput = productForm.querySelector('input[name="id"]');
      if (!variantInput || !variantInput.value) return;
      
      const submitButton = productForm.querySelector('[type="submit"]');
      if (!submitButton) return;
      
      const buttonText = submitButton.querySelector('span');
      const isShowingSelectSize = buttonText && (buttonText.textContent?.trim() === 'SELECT A SIZE');
      
      // If marked as single-variant OR no variant picker OR button shows "SELECT A SIZE"
      if (isSingleVariant || !variantSelects || isShowingSelectSize) {
        // For single-variant products, always enable if variant is available
        if (variantInput.value && !variantInput.hasAttribute('disabled')) {
          // Force enable button and fix text IMMEDIATELY
          submitButton.removeAttribute('disabled');
          submitButton.removeAttribute('aria-disabled');
          
          if (buttonText) {
            // Force correct button text - never show "SELECT A SIZE"
            if (productForm.hasAttribute('data-has-pre-order')) {
              buttonText.textContent = window.variantStrings?.preOrder || 'Pre-order';
            } else {
              buttonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
            }
          }
          
          // Mark as single-variant to prevent future changes
          productForm.setAttribute('data-single-variant', 'true');
          if (productInfo) productInfo.dataset.singleVariantProduct = 'true';
          
          // Don't call toggleSubmitButton for single-variant products - direct DOM manipulation is faster
        } else if (variantInput.hasAttribute('disabled')) {
          // Variant is sold out - set sold out message
          submitButton.setAttribute('disabled', 'disabled');
          submitButton.setAttribute('aria-disabled', 'true');
          if (buttonText) {
            buttonText.textContent = window.variantStrings?.soldOut || 'Sold Out';
          }
        }
      }
    });
  }
  
  // Run immediately when script loads (synchronously, no waiting)
  // BUT: Delay to allow Swym to initialize first
  if (document.body) {
    // Delay to allow Swym to attach to buttons
    setTimeout(fixSingleVariantButtons, 500);
  } else {
    // If body isn't ready, wait for it
    const bodyObserver = new MutationObserver(() => {
      if (document.body) {
        bodyObserver.disconnect();
        // Delay to allow Swym to attach to buttons
        setTimeout(fixSingleVariantButtons, 500);
      }
    });
    bodyObserver.observe(document.documentElement, { childList: true, subtree: true });
  }
  
  // Also run on DOMContentLoaded with delay
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(fixSingleVariantButtons, 800);
    }, { once: true });
  } else {
    setTimeout(fixSingleVariantButtons, 800);
  }
  
  // Run repeatedly to catch any late changes - but with delay to allow Swym first
  setTimeout(() => {
    const intervalId = setInterval(fixSingleVariantButtons, 50); // Check every 50ms
    // Clear after 30 seconds (longer to catch any late-running JavaScript)
    setTimeout(() => clearInterval(intervalId), 30000);
  }, 1000); // Delay initial start to allow Swym to initialize
  
  // Also watch for new elements being added
  const observer = new MutationObserver((mutations) => {
    let shouldCheck = false;
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1 && (
          node.matches?.('product-info') ||
          node.querySelector?.('product-info') ||
          node.matches?.('product-form') ||
          node.querySelector?.('product-form')
        )) {
          shouldCheck = true;
        }
      });
    });
    if (shouldCheck) {
      setTimeout(fixSingleVariantButtons, 500); // Delay to allow Swym to attach first
    }
  });
  
  observer.observe(document.body || document.documentElement, {
    childList: true,
    subtree: true
  });
})();

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = []
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute(
          'form',
          `${element.form.getAttribute('id')}-${uniqueKey}`
        );
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

class QuantityInputDawn extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (
        parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
        this.input.value == 0
      ) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);

    if (
      this.input.dataset.min === previousValue &&
      event.target.name === 'minus'
    ) {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle(
        'disabled',
        parseInt(value) <= parseInt(this.input.min)
      );
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input-dawn', QuantityInputDawn);

class ProductVariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');

      // change previous target.selectedOptions with new value
      const newSelectedOption = Array.from(target.options).find(
        (option) => option.value === value
      );
      if (newSelectedOption) {
        newSelectedOption.setAttribute('selected', 'selected');
      }

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .product-option__swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty(
          '--swatch--background',
          swatchValue
        );
        selectedDropdownSwatchValue.classList.remove(
          'product-option__swatch--unavailable'
        );
      } else {
        selectedDropdownSwatchValue.style.setProperty(
          '--swatch--background',
          'unset'
        );
        selectedDropdownSwatchValue.classList.add(
          'product-option__swatch--unavailable'
        );
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target
        .closest(`.product-form__input`)
        .querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll('select option[selected], fieldset input:checked')
    ).map(({ dataset }) => dataset.optionValueId);
  }
}
customElements.define('product-variant-selects', ProductVariantSelects);

if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
        this.productMediaGallery = this.querySelector(
          `[id^="MediaGallery-${this.dataset.section}"]`
        );
        this.productVariantSelects = this.querySelector(
          'product-variant-selects'
        );
      }

      connectedCallback() {
        // Ensure form action is removed immediately when element is connected
        const form = this.querySelector('form');
        if (form) {
          // Remove action attribute immediately
          form.removeAttribute('action');
          
          // Watch for any action attribute being re-added
          const observer = new MutationObserver(() => {
            if (form.hasAttribute('action')) {
              form.removeAttribute('action');
            }
          });
          observer.observe(form, {
            attributes: true,
            attributeFilter: ['action']
          });
          this.formActionObserver = observer;
        }
        
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(
          new CustomEvent('product-info:loaded', { bubbles: true })
        );
        this.initUpdateMedia();

        if (
          this.productMediaGallery &&
          this.productMediaGallery.dataset.hideOtherVariantsMedia === 'true'
        ) {
          this.updateVariantMedia();
        }

        // Delay initialization to allow Swym and other third-party scripts to initialize first
        // Swym initializes on page load, so we wait a bit before modifying buttons
        setTimeout(() => {
          this.initSingleVariantProduct();
        }, 500); // Increased delay to allow Swym to initialize
        
        // Also run after DOM is fully loaded with additional delay
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => {
            setTimeout(() => {
              this.initSingleVariantProduct();
            }, 800); // Additional delay for Swym
          });
        } else {
          setTimeout(() => {
            this.initSingleVariantProduct();
          }, 800); // Additional delay for Swym
        }
        
        // Set up observer to watch for button changes (but delay this too)
        setTimeout(() => {
          this.setupButtonWatcher();
        }, 1000); // Delay watcher setup to allow Swym to attach
      }
      
        setupButtonWatcher() {
          const productForm = this.productForm;
          if (!productForm) return;
          
          const submitButton = productForm.querySelector('[type="submit"]');
          if (!submitButton) return;
          
          const variantInput = productForm.querySelector('input[name="id"]');
          if (!variantInput || !variantInput.value) return;
          
          const variantSelects = this.productVariantSelects;
          const isSingleVariant = productForm.hasAttribute('data-single-variant') ||
                                 productForm.dataset.singleVariant === 'true' ||
                                 !variantSelects;
          
          // Only watch if it's a single-variant product
          if (isSingleVariant) {
            const isVariantAvailable = !variantInput.hasAttribute('disabled');
            
            // Lock button state for single-variant products
            // Watch for ANY changes and revert them immediately
            const observer = new MutationObserver(() => {
              // Don't interfere if button is in loading state or if we're currently loading
              if (submitButton.classList.contains('loading') || this.isLoading) {
                return; // Leave loading state alone
              }
              
              const buttonText = submitButton.querySelector('span');
              const currentText = buttonText?.textContent?.trim();
              
              // Don't interfere if spinner is showing (check for spinner HTML)
              if (buttonText && (buttonText.innerHTML.includes('lds-ring') || buttonText.innerHTML.includes('button-spinner'))) {
                return; // Leave spinner alone
              }
              
              // If button shows "SELECT A SIZE" or gets disabled when variant is available, fix it immediately
              if (isVariantAvailable && variantInput.value) {
                if (currentText === 'SELECT A SIZE' || 
                    submitButton.hasAttribute('disabled') || 
                    submitButton.hasAttribute('aria-disabled')) {
                  // Force correct state
                  submitButton.removeAttribute('disabled');
                  submitButton.removeAttribute('aria-disabled');
                  if (buttonText) {
                    if (productForm.hasAttribute('data-has-pre-order')) {
                      buttonText.textContent = window.variantStrings?.preOrder || 'Pre-order';
                    } else {
                      buttonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
                    }
                  }
                }
              }
            });
            
            // Observe button and button text for ANY changes
            observer.observe(submitButton, {
              attributes: true,
              attributeFilter: ['disabled', 'aria-disabled'],
              childList: true,
              subtree: true,
              characterData: true
            });
            
            // Also observe the text span directly
            const textSpan = submitButton.querySelector('span');
            if (textSpan) {
              observer.observe(textSpan, {
                childList: true,
                subtree: true,
                characterData: true
              });
            }
            
            // Store observer so we can disconnect later
            this.buttonWatcher = observer;
            
            // Also lock the button directly - prevent direct property changes
            // But allow loading state changes
            const originalDisabled = submitButton.disabled;
            Object.defineProperty(submitButton, 'disabled', {
              get: function() {
                // If loading, return false (enabled) so spinner shows
                if (this.classList.contains('loading')) {
                  return false;
                }
                return isVariantAvailable ? false : this.hasAttribute('disabled');
              },
              set: function(value) {
                // Don't interfere if button is loading
                if (this.classList.contains('loading')) {
                  return;
                }
                if (isVariantAvailable && value) {
                  // Ignore disable attempts for available variants
                  this.removeAttribute('disabled');
                  this.removeAttribute('aria-disabled');
                } else {
                  this.setAttribute('disabled', value ? 'disabled' : '');
                }
              },
              configurable: true
            });
          }
        }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      initUpdateMedia() {
        const variantIdFromUrl =
          parseInt(
            window.location.search
              .split('?')
              .filter((item) => item.includes('variant='))[0]
              ?.split('=')[1]
          ) || null;
        if (!variantIdFromUrl) {
          return;
        }
        const variant = this.getSelectedVariant(this);
        if (!variant || !variantIdFromUrl) {
          return;
        }
        if (variant?.id === variantIdFromUrl) {
          this.updateMedia(variant?.featured_media?.id);
        }
      }

      initSingleVariantProduct() {
        const productForm = this.productForm;
        if (!productForm) return;

        // Get the variant ID from the form input (already set by Liquid)
        const variantInput = productForm.querySelector('input[name="id"]');
        if (!variantInput) return;

        const variantId = variantInput.value?.trim();
        
        const submitButton = productForm.querySelector('[type="submit"]');
        if (!submitButton) return;

        const variantSelects = this.productVariantSelects;
        
        // Check if product has only one total variant
        let hasOnlyOneVariant = false;
        let singleVariant = null;
        
        if (variantSelects) {
          // Check variants data
          const allVariantsEl = variantSelects.querySelector('[data-all-variants]');
          if (allVariantsEl) {
            try {
              const allVariants = JSON.parse(allVariantsEl.textContent);
              if (allVariants.length === 1) {
                hasOnlyOneVariant = true;
                singleVariant = allVariants[0];
                
                // Auto-select all options for this single variant
                const selects = variantSelects.querySelectorAll('select');
                const radioFieldsets = variantSelects.querySelectorAll('fieldset');
                
                // Auto-select in selects
                selects.forEach((select, index) => {
                  if (select.selectedIndex === -1 || select.selectedIndex === 0) {
                    // Find the option that matches the single variant's option value
                    const variantOptionValue = singleVariant.options?.[index];
                    if (variantOptionValue) {
                      for (let i = 0; i < select.options.length; i++) {
                        const option = select.options[i];
                        const optionText = option.textContent?.trim() || option.value?.trim();
                        if (optionText === variantOptionValue || option.value === variantOptionValue) {
                          select.selectedIndex = i;
                          select.dispatchEvent(new Event('change', { bubbles: true }));
                          break;
                        }
                      }
                    } else if (select.options.length > 1) {
                      // Just select the first non-empty option
                      select.selectedIndex = 1;
                      select.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                  }
                });
                
                // Auto-select in radio fieldsets
                radioFieldsets.forEach((fieldset, index) => {
                  const variantOptionValue = singleVariant.options?.[index];
                  const radioButtons = fieldset.querySelectorAll('input[type="radio"]');
                  
                  if (variantOptionValue && radioButtons.length > 1) {
                    // Find the radio button that matches
                    radioButtons.forEach((radio) => {
                      if (radio.value === variantOptionValue || radio.value.trim() === variantOptionValue.trim()) {
                        if (!radio.checked) {
                          radio.checked = true;
                          radio.dispatchEvent(new Event('change', { bubbles: true }));
                        }
                      }
                    });
                  } else if (radioButtons.length === 1) {
                    // Only one option - select it
                    const firstRadio = radioButtons[0];
                    if (!firstRadio.checked) {
                      firstRadio.checked = true;
                      firstRadio.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                  }
                });
                
                // Update variant input with the single variant ID
                if (singleVariant.id) {
                  this.updateVariantInputs(singleVariant.id);
                  variantInput.value = singleVariant.id.toString();
                  variantInput.removeAttribute('disabled');
                }
              }
            } catch (e) {
              // If parsing fails, continue
            }
          }
        }
        
        // Use variant ID from input or from single variant
        const finalVariantId = variantId || (singleVariant?.id?.toString());
        if (!finalVariantId) return;
        
        // SIMPLE CASE: If there's a variant ID and no variant picker, it's definitely a single-variant product
        // OR if there's only one variant total, treat it as single-variant
        if (!variantSelects || hasOnlyOneVariant) {
          const variantToUse = singleVariant || { id: finalVariantId, available: true };
          const isVariantAvailable = !variantInput.hasAttribute('disabled') && 
                                     (variantToUse.available !== false);
          
          if (isVariantAvailable) {
            variantInput.removeAttribute('disabled');
            if (singleVariant?.id) {
              variantInput.value = singleVariant.id.toString();
            }
            
            // Force enable the button - override any previous state
            const buttonText = submitButton.querySelector('span');
            if (buttonText) {
              // Set correct button text
              if (productForm.hasAttribute('data-has-pre-order')) {
                buttonText.textContent = window.variantStrings?.preOrder || 'Pre-order';
              } else {
                buttonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
              }
            }
            
            // Force remove disabled attributes
            submitButton.removeAttribute('disabled');
            submitButton.removeAttribute('aria-disabled');
            // Then call toggleSubmitButton to ensure proper state
            // Safety check: ensure ProductForm is fully initialized before calling methods
            if (productForm && typeof productForm.toggleSubmitButton === 'function') {
              productForm.toggleSubmitButton(false);
            }
            this.dataset.singleVariantProduct = 'true';
          } else {
            // Safety check: ensure ProductForm is fully initialized before calling methods
            if (productForm && typeof productForm.toggleSubmitButton === 'function') {
              productForm.toggleSubmitButton(true, window.variantStrings?.soldOut || 'Sold Out');
            } else {
              // Fallback: directly manipulate button if method not available yet
              submitButton.setAttribute('disabled', 'disabled');
              submitButton.setAttribute('aria-disabled', 'true');
              const buttonText = submitButton.querySelector('span');
              if (buttonText) {
                buttonText.textContent = window.variantStrings?.soldOut || 'Sold Out';
              }
            }
          }
          return;
        }
        
        // Check if this is a product with only one total variant
        // Note: hasOnlyOneVariant is already declared earlier in this function - reuse it
        
        if (variantSelects && !hasOnlyOneVariant) {
          // Product has variant picker - check if there's only one total variant
          const allVariantsEl = variantSelects.querySelector('[data-all-variants]');
          if (allVariantsEl) {
            try {
              const allVariants = JSON.parse(allVariantsEl.textContent);
              // If there's only one variant total, auto-select it
              if (allVariants.length === 1) {
                hasOnlyOneVariant = true;
                // Auto-select the first (and only) option in each group
                const selects = variantSelects.querySelectorAll('select');
                const radioFieldsets = variantSelects.querySelectorAll('fieldset');
                
                // Auto-select in selects
                selects.forEach((select) => {
                  if (select.selectedIndex === -1 || select.selectedIndex === 0) {
                    select.selectedIndex = 0;
                    select.dispatchEvent(new Event('change', { bubbles: true }));
                  }
                });
                
                // Auto-select in radio fieldsets
                radioFieldsets.forEach((fieldset) => {
                  const firstRadio = fieldset.querySelector('input[type="radio"]');
                  if (firstRadio && !firstRadio.checked) {
                    firstRadio.checked = true;
                    firstRadio.dispatchEvent(new Event('change', { bubbles: true }));
                  }
                });
              }
            } catch (e) {
              // If parsing fails, continue with single-variant check
            }
          }
        }
        
        // Check if this is a single-variant product (no variant picker) OR has only one total variant
        const hasVariantPicker = !!variantSelects;
        if (hasVariantPicker && !hasOnlyOneVariant) {
          // Has variant picker and multiple variants - auto-select options if each group has only one option
          const selects = variantSelects.querySelectorAll('select');
          const radioFieldsets = variantSelects.querySelectorAll('fieldset');
          
          let autoSelectedOptions = false;
          
          // Auto-select in selects if they have only one option
          selects.forEach((select) => {
            if (select.options.length === 2 && select.selectedIndex === -1) {
              // Only one option (plus empty default), auto-select it
              select.selectedIndex = 1;
              select.dispatchEvent(new Event('change', { bubbles: true }));
              autoSelectedOptions = true;
            } else if (select.selectedIndex === -1 || !select.selectedOptions[0]) {
              // Has multiple options but none selected - check if we can auto-select first available
              // For now, don't auto-select if there are multiple options
            }
          });
          
          // Auto-select in radio fieldsets if they have only one radio button
          radioFieldsets.forEach((fieldset) => {
            const radioButtons = fieldset.querySelectorAll('input[type="radio"]');
            if (radioButtons.length === 1) {
              // Only one option, auto-select it
              const firstRadio = radioButtons[0];
              if (!firstRadio.checked) {
                firstRadio.checked = true;
                firstRadio.dispatchEvent(new Event('change', { bubbles: true }));
                autoSelectedOptions = true;
              }
            }
          });
          
          // If we auto-selected options, wait a bit for change handlers to process, then enable button
          if (autoSelectedOptions) {
            setTimeout(() => {
              // Re-check if all options are now selected
              let allOptionsSelected = true;
              selects.forEach((select) => {
                if (select.selectedIndex === -1 || !select.selectedOptions[0]) {
                  allOptionsSelected = false;
                }
              });
              radioFieldsets.forEach((fieldset) => {
                const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
                if (!checkedInput) {
                  allOptionsSelected = false;
                }
              });
              
              // If all options are selected, enable the button
              if (allOptionsSelected) {
                const isVariantAvailable = !variantInput.hasAttribute('disabled');
                if (isVariantAvailable) {
                  variantInput.removeAttribute('disabled');
                  if (productForm && typeof productForm.toggleSubmitButton === 'function') {
                    productForm.toggleSubmitButton(false);
                  }
                  this.dataset.singleVariantProduct = 'true';
                }
              }
            }, 100);
            return; // Wait for handlers to process
          }
          
          // Check if all options are already selected (without auto-selection)
          let allOptionsSelected = true;
          selects.forEach((select) => {
            if (select.selectedIndex === -1 || !select.selectedOptions[0]) {
              allOptionsSelected = false;
            }
          });
          radioFieldsets.forEach((fieldset) => {
            const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
            if (!checkedInput) {
              allOptionsSelected = false;
            }
          });
          
          // If all options are selected (already were), enable the button immediately
          if (allOptionsSelected) {
            const isVariantAvailable = !variantInput.hasAttribute('disabled');
            if (isVariantAvailable) {
              variantInput.removeAttribute('disabled');
              // Safety check: ensure ProductForm is fully initialized before calling methods
              if (productForm && typeof productForm.toggleSubmitButton === 'function') {
                productForm.toggleSubmitButton(false);
              } else {
                // Fallback: directly manipulate button if method not available yet
                const submitButton = productForm?.querySelector('[type="submit"]');
                if (submitButton) {
                  submitButton.removeAttribute('disabled');
                  submitButton.removeAttribute('aria-disabled');
                }
              }
              this.dataset.singleVariantProduct = 'true';
              return;
            }
          }
          return; // Multiple variants with selection needed
        }
        
        // Single-variant product or only one total variant - auto-enable
        // Check variant availability BEFORE removing disabled attribute
        const isVariantAvailable = !variantInput.hasAttribute('disabled');
        
        // Update variant inputs to ensure consistency
        this.updateVariantInputs(variantId);
        
        // Set button state based on variant availability
        if (isVariantAvailable) {
          // Variant is available - ensure variant input is enabled and button is enabled
          variantInput.removeAttribute('disabled');
          // Safety check: ensure ProductForm is fully initialized before calling methods
          if (productForm && typeof productForm.toggleSubmitButton === 'function') {
            productForm.toggleSubmitButton(false);
          } else {
            // Fallback: directly manipulate button if method not available yet
            const submitButton = productForm?.querySelector('[type="submit"]');
            if (submitButton) {
              submitButton.removeAttribute('disabled');
              submitButton.removeAttribute('aria-disabled');
            }
          }
        } else {
          // Variant is not available - keep input disabled and button disabled with sold out message
          if (productForm && typeof productForm.toggleSubmitButton === 'function') {
            productForm.toggleSubmitButton(true, window.variantStrings?.soldOut || 'Sold Out');
          } else {
            // Fallback: directly manipulate button if method not available yet
            const submitButton = productForm?.querySelector('[type="submit"]');
            if (submitButton) {
              submitButton.setAttribute('disabled', 'disabled');
              submitButton.setAttribute('aria-disabled', 'true');
              const buttonText = submitButton.querySelector('span');
              if (buttonText) {
                buttonText.textContent = window.variantStrings?.soldOut || 'Sold Out';
              }
            }
          }
        }
        
        // Mark this as a single-variant product to prevent other code from disabling the button
        this.dataset.singleVariantProduct = 'true';
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        // Disconnect button watcher if it exists
        if (this.buttonWatcher) {
          this.buttonWatcher.disconnect();
        }
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll('.scroll-trigger')
            .forEach((element) =>
              element.classList.add('scroll-trigger--cancel')
            )
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (!this.contains(event.target)) return;
        
        // COMPLETELY SKIP for single-variant products - don't touch them at all
        const productForm = this.productForm;
        if (productForm && (productForm.hasAttribute('data-single-variant') || 
                           productForm.dataset.singleVariant === 'true')) {
          return; // Do nothing for single-variant products
        }

        // Immediately check variant and enable button before async fetch
        // This also updates the variant input immediately
        this.updateButtonStateImmediately(selectedOptionValues);

        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      updateButtonStateImmediately(selectedOptionValues) {
        const productForm = this.productForm;
        const variantSelects = this.productVariantSelects;
        const productInfo = this; // Store reference to this ProductInfo instance

        // For single-variant products, skip this logic completely
        if (!productForm) {
          return;
        }
        
        // Check if it's a single-variant product (by data attribute or no variant picker)
        const isSingleVariant = productForm.hasAttribute('data-single-variant') ||
                               productForm.dataset.singleVariant === 'true' ||
                               !variantSelects;
        
        if (isSingleVariant) {
          // Single-variant product - skip all this logic, button is already correctly set in Liquid
          return;
        }

        // Use setTimeout(0) to check after current event loop completes
        // This ensures the DOM is fully updated but still happens immediately
        setTimeout(() => {
          const allVariantsEl = variantSelects.querySelector(
            '[data-all-variants]'
          );
          if (!allVariantsEl) return;

          try {
            const allVariants = JSON.parse(allVariantsEl.textContent);

            // Get currently selected option text values from DOM
            // Check both selects and radio inputs
            const selects = variantSelects.querySelectorAll('select');
            const radioFieldsets = variantSelects.querySelectorAll('fieldset');

            const selectedOptions = [];

            // Get values from selects
            selects.forEach((select) => {
              const selectedOption = select.selectedOptions[0];
              if (selectedOption) {
                const value =
                  selectedOption.textContent?.trim() ||
                  selectedOption.value?.trim();
                if (value) selectedOptions.push(value);
              }
            });

            // Get values from radio button fieldsets
            radioFieldsets.forEach((fieldset) => {
              const checkedInput = fieldset.querySelector(
                'input[type="radio"]:checked'
              );
              if (checkedInput) {
                const value = checkedInput.value?.trim();
                if (value) selectedOptions.push(value);
              }
            });

            // Count total option groups
            const totalOptions = variantSelects.querySelectorAll(
              '.product-form__input, fieldset'
            ).length;

            // If all options are selected, find matching variant
            if (
              selectedOptions.length === totalOptions &&
              selectedOptions.length > 0
            ) {
              const matchingVariant = allVariants.find((variant) => {
                if (
                  !variant.options ||
                  variant.options.length !== selectedOptions.length
                ) {
                  return false;
                }
                // Match by comparing option values (case-insensitive, trimmed)
                return variant.options.every((option, index) => {
                  const variantOption = option?.toString().trim().toLowerCase();
                  const selectedOption = selectedOptions[index]?.toLowerCase();
                  return variantOption === selectedOption;
                });
              });

              if (matchingVariant) {
                // Immediately update variant input when a matching variant is found
                // This ensures the correct variant ID is set before async fetch completes
                if (
                  productInfo &&
                  typeof productInfo.updateVariantInputs === 'function'
                ) {
                  productInfo.updateVariantInputs(matchingVariant.id);
                }

                if (matchingVariant.available) {
                  if (productForm && typeof productForm.toggleSubmitButton === 'function') {
                    productForm.toggleSubmitButton(false);
                  }
                } else {
                  if (productForm && typeof productForm.toggleSubmitButton === 'function') {
                    productForm.toggleSubmitButton(
                      true,
                      window.variantStrings?.soldOut || 'Sold Out'
                    );
                  }
                }
                return;
              }
            }

            // If some but not all options selected, disable button
            // BUT: Skip for single-variant products
            if (
              selectedOptions.length > 0 &&
              selectedOptions.length < totalOptions
            ) {
              // Check if this is a single-variant product - don't disable if it is
              const isSingleVariant = productInfo.dataset.singleVariantProduct === 'true' ||
                                     productForm.hasAttribute('data-single-variant') ||
                                     productForm.dataset.singleVariant === 'true' ||
                                     !variantSelects ||
                                     (allVariants.length === 1);
              if (!isSingleVariant) {
                if (productForm && typeof productForm.toggleSubmitButton === 'function') {
                  productForm.toggleSubmitButton(true, 'SELECT A SIZE');
                }
              }
              return;
            }

            // No options selected - disable button
            // BUT: Skip for single-variant products
            if (selectedOptions.length === 0) {
              // Check if this is a single-variant product - don't disable if it is
              const isSingleVariant = productInfo.dataset.singleVariantProduct === 'true' ||
                                     productForm.hasAttribute('data-single-variant') ||
                                     productForm.dataset.singleVariant === 'true' ||
                                     !variantSelects ||
                                     (allVariants.length === 1);
              if (!isSingleVariant) {
                if (productForm && typeof productForm.toggleSubmitButton === 'function') {
                  productForm.toggleSubmitButton(true, 'SELECT A SIZE');
                }
              } else {
                // Single-variant product - ensure button is enabled
                const variantInput = productForm.querySelector('input[name="id"]');
                if (variantInput && variantInput.value && !variantInput.hasAttribute('disabled')) {
                  if (productForm && typeof productForm.toggleSubmitButton === 'function') {
                    productForm.toggleSubmitButton(false);
                  }
                }
              }
            }
          } catch (e) {
            // Silently fail - fallback to async update
          }
        }, 0);
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.handleErrorMessage();
        // Button state is already updated immediately in updateButtonStateImmediately()
        // which is called before this function, so we don't disable it here
        
        // COMPLETELY SKIP for single-variant products - don't touch button state
        if (productForm && (productForm.hasAttribute('data-single-variant') || 
                           productForm.dataset.singleVariant === 'true')) {
          return; // Do nothing - button state is controlled by Liquid
        }
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML =
              html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              'text/html'
            );
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              // console.log("Fetch aborted by user");
            } else {
              // console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          'product-variant-selects [data-selected-variant]'
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const productVariantSelects = html.querySelector(
          'product-variant-selects'
        );
        if (productVariantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            productVariantSelects,
            this.preProcessHtmlCallbacks
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          // Get variant from fetched HTML first
          const fetchedVariant = this.getSelectedVariant(html);

          // But also check the current variant input to see what was immediately set
          // This prevents stale async responses from overwriting newer selections
          const currentVariantInput =
            this.productForm?.variantIdInput?.value?.trim();

          // Try to get variant from current DOM state based on selected options
          let variant = this.getSelectedVariant(this);

          // If current DOM doesn't have variant data, or if current input is different from fetched variant,
          // calculate variant from currently selected options to ensure we use the most recent selection
          if (
            !variant ||
            (currentVariantInput &&
              fetchedVariant &&
              currentVariantInput !== fetchedVariant.id.toString())
          ) {
            // User has selected a different size, calculate from current DOM state
            const variantSelects = this.productVariantSelects;
            if (variantSelects) {
              const allVariantsEl = variantSelects.querySelector(
                '[data-all-variants]'
              );
              if (allVariantsEl) {
                try {
                  const allVariants = JSON.parse(allVariantsEl.textContent);
                  const selects = variantSelects.querySelectorAll('select');
                  const radioFieldsets =
                    variantSelects.querySelectorAll('fieldset');
                  const selectedOptions = [];

                  selects.forEach((select) => {
                    const selectedOption = select.selectedOptions[0];
                    if (selectedOption) {
                      const value =
                        selectedOption.textContent?.trim() ||
                        selectedOption.value?.trim();
                      if (value) selectedOptions.push(value);
                    }
                  });

                  radioFieldsets.forEach((fieldset) => {
                    const checkedInput = fieldset.querySelector(
                      'input[type="radio"]:checked'
                    );
                    if (checkedInput) {
                      const value = checkedInput.value?.trim();
                      if (value) selectedOptions.push(value);
                    }
                  });

                  const totalOptions = variantSelects.querySelectorAll(
                    '.product-form__input, fieldset'
                  ).length;

                  if (
                    selectedOptions.length === totalOptions &&
                    selectedOptions.length > 0
                  ) {
                    const matchingVariant = allVariants.find((v) => {
                      if (
                        !v.options ||
                        v.options.length !== selectedOptions.length
                      )
                        return false;
                      return v.options.every((option, index) => {
                        const variantOption = option
                          ?.toString()
                          .trim()
                          .toLowerCase();
                        const selectedOption =
                          selectedOptions[index]?.toLowerCase();
                        return variantOption === selectedOption;
                      });
                    });

                    if (matchingVariant) {
                      variant = matchingVariant;
                    }
                  }
                } catch (e) {
                  // Fallback to fetched variant on error
                  variant = fetchedVariant;
                }
              } else {
                variant = fetchedVariant;
              }
            } else {
              variant = fetchedVariant;
            }
          }

          // If still no variant, use fetched variant
          if (!variant) {
            variant = fetchedVariant;
          }

          // this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          // Update variant input only if it's different from what's already set
          // This prevents overwriting newer selections with stale async responses
          if (variant?.id) {
            if (
              !currentVariantInput ||
              currentVariantInput !== variant.id.toString()
            ) {
              this.updateVariantInputs(variant.id);
            }
          } else {
            this.updateVariantInputs(variant?.id);
          }
          this.updateVariantDiscountBadge(
            variant?.price,
            variant?.compare_at_price
          );
          this.updateVariantDetails(variant?.id);
          // Inserted logic for pickupAvailability after updateVariantDetails
          if (this.pickupAvailability && variant?.id) {
            this.pickupAvailability.dataset.variantId = variant.id;
            this.pickupAvailability.fetchAvailability(variant.id);
          }

          if (
            this.productMediaGallery &&
            this.productMediaGallery.dataset.hideOtherVariantsMedia === 'true'
          ) {
            this.updateVariantMedia(html);
          }

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(variant?.featured_media?.id);

          const updateSourceFromDestination = (
            id,
            shouldHide = (source) => false
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(
              `#${id}-${this.dataset.section}`
            );

            if (source && destination) {
              destination.innerHTML = source.innerHTML;

              if (id === 'Inventory') {
                const inventoryThreshold = parseInt(
                  source.dataset.inventoryThreshold
                );
                const inventoryQuantity = parseInt(
                  source.dataset.inventoryQuantity
                );
                destination.classList.toggle(
                  'hidden',
                  (inventoryThreshold &&
                    inventoryQuantity > inventoryThreshold) ||
                    inventoryQuantity < 1
                );
              } else {
                destination.classList.toggle('hidden', shouldHide(source));
              }
            }
          };

          updateSourceFromDestination('ProductPurchaseInfo');
          updateSourceFromDestination('ProductStickyCart');

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) =>
            classList.contains('hidden')
          );

          updateSourceFromDestination(
            'Inventory',
            ({ innerText }) => innerText === ''
          );

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`
          )?.classList.remove('hidden');

          // SKIP button state update for single-variant products
          const isSingleVariant = this.productForm?.hasAttribute('data-single-variant') ||
                                 this.productForm?.dataset.singleVariant === 'true';
          
          if (!isSingleVariant) {
            // Only update button state for multi-variant products
            if (this.productForm && typeof this.productForm.toggleSubmitButton === 'function') {
              this.productForm.toggleSubmitButton(
                html
                  .getElementById(`ProductSubmitButton-${this.sectionId}`)
                  ?.hasAttribute('disabled') ?? true,
                window.variantStrings.soldOut
              );
            }
          }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantMedia() {
        const select_variant_options_handle = this.querySelector(
          'product-variant-selects [data-selected-variant-options-handle]'
        )?.innerHTML.trim();

        if (!select_variant_options_handle) {
          const productAllVariants = this.productVariantSelects
            .querySelector('[data-all-variants]')
            .innerHTML.trim();

          // Make sure to attach the event listener to the correct element
          this.productVariantSelects.addEventListener('click', (event) => {
            const closestVariantOptionInput = event.target.closest(
              '.product-option__input'
            );

            // Ensure that you are finding the correct input
            if (closestVariantOptionInput) {
              const closestVariantOptionInputValue =
                closestVariantOptionInput.value;

              // Parse productAllVariants and check for the selected variant
              const availableVariant = JSON.parse(productAllVariants).find(
                (variant) => {
                  return variant.options.some(
                    (option) => option === closestVariantOptionInputValue
                  );
                }
              );
            } else {
              // console.log('No variant option input found');
            }
          });
        }

        this.productMediaGallery
          .querySelectorAll('[data-media-group]')
          .forEach((element) => element.classList.add('hidden'));

        this.productMediaGallery
          .querySelectorAll(
            `[data-media-group="${select_variant_options_handle}"]`
          )
          .forEach((element) => element.classList.remove('hidden'));

        this.productMediaGallery
          .querySelectorAll('swiper-product-gallery')
          .forEach((swiperSlide) => {
            if (swiperSlide.instance) {
              swiperSlide.instance.update(); // Update the Swiper instance
            } else {
              swiperSlide.init(); // If the instance is not available, reinitialize
            }
          });
      }

      updateVariantOptionMedia() {
        this.productMediaGallery
          .querySelectorAll('[data-media-group]:not(.hidden)')
          .forEach((element) => {});
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return; // Guard against null input
          input.value = variantId ?? '';
          // Ensure input is enabled so it can be submitted
          input.removeAttribute('disabled');
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState(
          {},
          '',
          `${url}${variantId ? `?variant=${variantId}` : ''}`
        );
      }

      updateSellingPlanURL(sellingPlanId, selectedVariantId) {
        if (!selectedVariantId) return;
        if (!sellingPlanId) {
          window.history.replaceState(
            {},
            '',
            `${window.location.href.replace(/&selling_plan=\d+/, '')}`
          );
        } else {
          const params = new URLSearchParams(window.location.search);
          params[params.has('variant') ? 'set' : 'append'](
            'variant',
            selectedVariantId
          );
          if (sellingPlanId) {
            params[params.has('selling_plan') ? 'set' : 'append'](
              'selling_plan',
              sellingPlanId
            );
          } else {
            params.delete('selling_plan');
          }
          window.history.replaceState(
            {},
            '',
            `${this.dataset.url}?${params.toString()}`
          );
        }
      }

      updateVariantDetails(variantId) {
        const productVariantDetails = this.querySelectorAll(
          '.product__metafield_for_variant-block'
        );
        if (productVariantDetails) {
          productVariantDetails.forEach((productVariantDetail) => {
            const block_id = productVariantDetail.getAttribute('data-block-id');
            const metafieldDataElement = this.querySelector(
              `#product__metafield_for_variant-block--${block_id}`
            );
            if (!metafieldDataElement) {
              return;
            }
            try {
              let productVariantDetailsData = JSON.parse(
                metafieldDataElement?.textContent
              );
              productVariantDetail.innerHTML =
                productVariantDetailsData?.[variantId]?.replaceAll(
                  '\n',
                  '<br />'
                ) || '';
            } catch (error) {
              console.error('Error parsing JSON', error);
            }
          });
        }
      }

      updateVariantDiscountBadge(price, compare_at_price) {
        const discountBadge = this.querySelector(
          '.product-card__badge--discount'
        );
        if (!discountBadge) return;

        if (compare_at_price > price) {
          discountBadge.classList.remove('hidden');
          const percentageElement = discountBadge.querySelector('.percentage');
          // calculate discount percentage
          const discountPercentage = Math.round(
            ((compare_at_price - price) / compare_at_price) * 100
          );
          // update discount percentage
          percentageElement.textContent = discountPercentage;
          
          // Apply gradient based on discount percentage
          this.applyDiscountGradient(discountBadge, discountPercentage);
        } else {
          discountBadge.classList.add('hidden');
        }
      }

      applyDiscountGradient(badge, percentage) {
        const root = document.documentElement;
        let gradient = '';
        
        if (percentage >= 70 && percentage <= 80) {
          gradient = getComputedStyle(root).getPropertyValue('--sale-gradient-70-80').trim();
        } else if (percentage >= 50 && percentage <= 60) {
          gradient = getComputedStyle(root).getPropertyValue('--sale-gradient-50-60').trim();
        } else if (percentage >= 20 && percentage <= 40) {
          gradient = getComputedStyle(root).getPropertyValue('--sale-gradient-20-40').trim();
        } else if (percentage === 10 || percentage < 20) {
          gradient = getComputedStyle(root).getPropertyValue('--sale-gradient-10').trim();
        }
        
        if (gradient) {
          badge.style.background = gradient;
          badge.style.border = 'none';
        }
      }

      setUnavailable() {
        // SKIP for single-variant products - don't change button state
        const productForm = this.productForm;
        const isSingleVariant = productForm?.hasAttribute('data-single-variant') ||
                               productForm?.dataset.singleVariant === 'true';
        
        if (isSingleVariant) {
          return; // Don't modify button for single-variant products
        }
        
        if (this.productForm && typeof this.productForm.toggleSubmitButton === 'function') {
          this.productForm.toggleSubmitButton(
            true,
            window.variantStrings.unavailable
          );
        }

        const selectors = ['price', 'Inventory', 'Sku', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        // if layout is grid, add .active class to the relevant swiper-slide
        const gridLayout = this.querySelector('.main-product__media--grid');
        if (gridLayout) {
          // remove active class from all swiper-slides
          gridLayout.querySelectorAll('.swiper-slide').forEach((item) => {
            item.classList.remove('active');
          });
          // add .active class to the swiper-slide with the matching data-media-id
          const activeSlide = gridLayout.querySelector(
            `.swiper-slide[data-media-id="${variantFeaturedMediaId}"]`
          );
          if (activeSlide) {
            activeSlide.classList.add('active');
          }
        }

        if (
          this.querySelector('.main-product__media')?.hasAttribute(
            'data-desktop-grid'
          ) &&
          window.innerWidth >= 750
        ) {
          this.scrollToImage(variantFeaturedMediaId);
        }

        const productMedia = this.querySelector('swiper-product-gallery');
        productMedia?.setActiveMedia(variantFeaturedMediaId);
      }

      scrollToImage(variantFeaturedMediaId) {
        this.querySelectorAll('[data-media-id]').forEach((img) => {
          if (img.getAttribute('data-media-id') == variantFeaturedMediaId) {
            img.scrollIntoView({
              block: 'end',
              behavior: 'smooth',
            });
          }
        });
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          '.quantity__rules-cart .loading__spinner'
        ).classList.remove('hidden');
        fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              'text/html'
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() =>
            this.querySelector(
              '.quantity__rules-cart .loading__spinner'
            ).classList.add('hidden')
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`
        );
        const selectors = [
          '.quantity__input',
          '.quantity__rules',
          '.quantity__label',
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = [
              'data-cart-quantity',
              'data-min',
              'data-max',
              'step',
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('product-variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
  
  // Global check for single-variant products after page load
  // This ensures buttons are enabled even if other code disables them
  function ensureSingleVariantProductsEnabled() {
    document.querySelectorAll('product-info').forEach((productInfo) => {
      // Check if it's a single-variant product (no variant picker)
      const variantSelects = productInfo.querySelector('product-variant-selects');
      const productForm = productInfo.querySelector('product-form');
      const variantInput = productForm?.querySelector('input[name="id"]');
      const submitButton = productForm?.querySelector('[type="submit"]');
      
      // Mark as single-variant if no picker exists
      if (!variantSelects && variantInput && variantInput.value) {
        productInfo.dataset.singleVariantProduct = 'true';
      }
      
      if (productInfo.dataset.singleVariantProduct === 'true') {
        if (variantInput && variantInput.value && !variantInput.hasAttribute('disabled') && submitButton) {
          // Force enable button for single-variant products
          if (submitButton.hasAttribute('disabled') || submitButton.hasAttribute('aria-disabled')) {
            submitButton.removeAttribute('disabled');
            submitButton.removeAttribute('aria-disabled');
            // Make sure button text is correct, not "SELECT A SIZE"
            const buttonText = submitButton.querySelector('span');
            if (buttonText) {
              const currentText = buttonText.textContent?.trim();
              if (currentText === 'SELECT A SIZE' || currentText === 'SELECT A SIZE') {
                if (productForm?.hasAttribute('data-has-pre-order')) {
                  buttonText.textContent = window.variantStrings?.preOrder || 'Pre-order';
                } else {
                  buttonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
                }
              }
            }
            if (typeof productForm?.toggleSubmitButton === 'function') {
              productForm.toggleSubmitButton(false);
            }
          }
        }
      }
    });
  }
  
  // Run check after page load and periodically to catch any late disabling
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(ensureSingleVariantProductsEnabled, 500);
    });
  } else {
    setTimeout(ensureSingleVariantProductsEnabled, 500);
  }
  
  // Also check periodically for single-variant products
  setInterval(ensureSingleVariantProductsEnabled, 1000);
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        
        // Ensure form exists before adding event listener
        if (!this.form) {
          console.error('ProductForm: form element not found');
          return;
        }
        
        // Store original action for Swym and other third-party scripts
        // Swym needs form[action*='/cart/add'] to find the button
        if (!this.form.hasAttribute('data-original-action')) {
          const originalAction = this.form.getAttribute('action') || this.form.action;
          if (originalAction && originalAction !== 'javascript:void(0)') {
            this.form.setAttribute('data-original-action', originalAction);
          } else {
            // If no action set, set it to cart/add for Swym compatibility
            this.form.setAttribute('data-original-action', '/cart/add');
          }
        }
        
        // Keep the action attribute for Swym compatibility, but prevent default submission
        // Set action to cart/add if it doesn't exist so Swym can find it
        if (!this.form.getAttribute('action') || this.form.getAttribute('action') === 'javascript:void(0)') {
          this.form.setAttribute('action', '/cart/add');
        }
        
        // Remove any default form action to prevent normal submission
        // Do this immediately and aggressively
        this.form.removeAttribute('action');
        this.form.setAttribute('data-no-submit', 'true');
        
        // Set form action to javascript:void(0) as a fallback
        this.form.action = 'javascript:void(0)';
        
        // BUT: Restore action attribute for Swym compatibility
        // Swym needs form[action*='/cart/add'] selector to work
        const originalAction = this.form.getAttribute('data-original-action') || '/cart/add';
        this.form.setAttribute('action', originalAction);
        
        // Prevent actual form submission via event handler instead
        
        this.variantIdInput.disabled = false;
        
        // Add submit handler with capture to catch early
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this), { capture: true });
        
        // Also add click handler on button as backup for single-variant products
        // Initialize submitButton through getter pattern
        this._submitButton = this.querySelector('[type="submit"]');
        if (this._submitButton) {
          const isSingleVariant = this.hasAttribute('data-single-variant');
          if (isSingleVariant) {
            // For single-variant products, intercept click early
            this._submitButton.addEventListener('click', (e) => {
              // Always prevent default for single-variant products
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              
              // Only proceed if form is valid
              if (this.form && this.form.checkValidity()) {
                // Manually trigger our submit handler
                this.onSubmitHandler(e);
              }
            }, { capture: true });
          }
        }
        
        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');
        this._submitButtonText = this._submitButton?.querySelector('span');

        if (document.querySelector('cart-drawer'))
          this._submitButton?.setAttribute('aria-haspopup', 'dialog');
        
        // Ensure button is always accessible for Swym and other third-party scripts
        // Watch for DOM changes to catch when Swym or other scripts modify the button
        if (this._submitButton) {
          // Refresh button reference after a short delay to allow Swym to inject
          setTimeout(() => {
            const currentButton = this.querySelector('[type="submit"]');
            if (currentButton && currentButton !== this._submitButton) {
              this._submitButton = currentButton;
              this._submitButtonText = currentButton.querySelector('span');
            }
          }, 1000);
          
          // Watch for changes to button container to help Swym find the button
          const buttonsContainer = this.querySelector('.product-form__buttons');
          if (buttonsContainer) {
            const observer = new MutationObserver(() => {
              // Refresh button reference when DOM changes
              const currentButton = this.querySelector('[type="submit"]');
              if (currentButton) {
                this._submitButton = currentButton;
                this._submitButtonText = currentButton.querySelector('span');
              }
            });
            
            observer.observe(buttonsContainer, {
              childList: true,
              subtree: true
            });
            
            // Store observer for cleanup
            this._buttonObserver = observer;
          }
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
        
        // Ensure cart drawer is initialized for single-variant products
        // Sometimes the cart drawer isn't found during initial construction
        if (!this.cart) {
          // Try to find it again after a short delay
          setTimeout(() => {
            this.cart = document.querySelector('cart-notification') ||
                       document.querySelector('cart-drawer');
          }, 100);
        }
      }
      
      connectedCallback() {
        // Ensure form action is preserved for Swym compatibility
        // Swym needs form[action*='/cart/add'] to find the button
        const form = this.querySelector('form');
        if (form) {
          // Store original action if not already stored
          if (!form.hasAttribute('data-original-action')) {
            const originalAction = form.getAttribute('action') || form.action;
            if (originalAction && originalAction !== 'javascript:void(0)') {
              form.setAttribute('data-original-action', originalAction);
            } else {
              form.setAttribute('data-original-action', '/cart/add');
            }
          }
          
          // Ensure action attribute exists for Swym (required for form[action*='/cart/add'] selector)
          const actionAttr = form.getAttribute('action');
          if (!actionAttr || actionAttr === 'javascript:void(0)') {
            form.setAttribute('action', '/cart/add');
          }
          
          // Mark as no-submit but keep action for Swym
          form.setAttribute('data-no-submit', 'true');
          
          // Watch for any action attribute being re-added or changed
          if (!this.formActionObserver) {
            const observer = new MutationObserver(() => {
              const currentAction = form.getAttribute('action');
              // If action is removed or set to javascript:void(0), restore it for Swym
              if (!currentAction || currentAction === 'javascript:void(0)') {
                const originalAction = form.getAttribute('data-original-action') || '/cart/add';
                form.setAttribute('action', originalAction);
              }
            });
            observer.observe(form, {
              attributes: true,
              attributeFilter: ['action']
            });
            this.formActionObserver = observer;
          }
        }
        
        // Ensure button reference is refreshed when element is connected
        // This helps Swym and other scripts find the button
        const currentButton = this.querySelector('[type="submit"]');
        if (currentButton) {
          this._submitButton = currentButton;
          this._submitButtonText = currentButton.querySelector('span');
        }
      }
      
      disconnectedCallback() {
        // Clean up observers
        if (this.formActionObserver) {
          this.formActionObserver.disconnect();
          this.formActionObserver = null;
        }
        if (this._buttonObserver) {
          this._buttonObserver.disconnect();
          this._buttonObserver = null;
        }
      }

      onSubmitHandler(evt) {
        // Always prevent default form submission
        evt.preventDefault();
        evt.stopPropagation();
        
        // Double-check: if form was already submitted, return early
        if (this.submitButton && this.submitButton.getAttribute('aria-disabled') === 'true') {
          return;
        }

        // Check if this is a single-variant product - handle it more directly
        const isSingleVariant = this.hasAttribute('data-single-variant') ||
                               this.dataset.singleVariant === 'true';
        
        // Debug logging
        if (isSingleVariant) {
          console.log('Single-variant product form submission intercepted');
        }
        
        // Always calculate variant ID from current DOM selection right before submitting
        // This ensures we use the most recent selection, not stale values from variant input
        const productInfo = this.closest('product-info');
        let variantId = null;

        // For single-variant products, always read directly from the variant input
        if (isSingleVariant) {
          variantId = this.variantIdInput?.value?.trim();
          
          if (!variantId) {
            // Single-variant product should always have a variant ID
            console.warn('Single-variant product missing variant ID');
            this.handleErrorMessage('Unable to add product to cart');
            return;
          }
          
          console.log('Single-variant product submitting with variant ID:', variantId);
          
          // Proceed directly to submit with the variant ID
          this.submitFormWithVariantId(variantId);
          return;
        }

        // For multi-variant products, calculate from selections
        if (productInfo) {
          const variantSelects = productInfo.querySelector(
            'product-variant-selects'
          );
          if (variantSelects) {
            const allVariantsEl = variantSelects.querySelector(
              '[data-all-variants]'
            );
            if (allVariantsEl) {
              try {
                const allVariants = JSON.parse(allVariantsEl.textContent);
                const selects = variantSelects.querySelectorAll('select');
                const radioFieldsets =
                  variantSelects.querySelectorAll('fieldset');
                const selectedOptions = [];

                selects.forEach((select) => {
                  const selectedOption = select.selectedOptions[0];
                  if (selectedOption) {
                    const value =
                      selectedOption.textContent?.trim() ||
                      selectedOption.value?.trim();
                    if (value) selectedOptions.push(value);
                  }
                });

                radioFieldsets.forEach((fieldset) => {
                  const checkedInput = fieldset.querySelector(
                    'input[type="radio"]:checked'
                  );
                  if (checkedInput) {
                    const value = checkedInput.value?.trim();
                    if (value) selectedOptions.push(value);
                  }
                });

                const totalOptions = variantSelects.querySelectorAll(
                  '.product-form__input, fieldset'
                ).length;

                if (
                  selectedOptions.length === totalOptions &&
                  selectedOptions.length > 0
                ) {
                  const matchingVariant = allVariants.find((v) => {
                    if (
                      !v.options ||
                      v.options.length !== selectedOptions.length
                    )
                      return false;
                    return v.options.every((option, index) => {
                      const variantOption = option
                        ?.toString()
                        .trim()
                        .toLowerCase();
                      const selectedOption =
                        selectedOptions[index]?.toLowerCase();
                      return variantOption === selectedOption;
                    });
                  });

                  if (matchingVariant && matchingVariant.id) {
                    variantId = matchingVariant.id.toString();
                    // Update variant input with calculated variant
                    if (
                      productInfo &&
                      typeof productInfo.updateVariantInputs === 'function'
                    ) {
                      productInfo.updateVariantInputs(matchingVariant.id);
                    }
                  }
                }
              } catch (e) {
                // Fallback to reading from input
                variantId = this.variantIdInput?.value?.trim();
              }
            } else {
              // Fallback to reading from input
              variantId = this.variantIdInput?.value?.trim();
            }
          } else {
            // Fallback to reading from input
            variantId = this.variantIdInput?.value?.trim();
          }
        } else {
          // Fallback to reading from input
          variantId = this.variantIdInput?.value?.trim();
        }

        if (!variantId) {
          this.handleErrorMessage('Please select a size');
          return;
        }

        this.submitFormWithVariantId(variantId);
      }

      submitFormWithVariantId(variantId) {
        if (!variantId) {
          this.handleErrorMessage('Please select a size');
          return;
        }

        this.handleErrorMessage();

        // Ensure cart drawer is found right before submitting
        // This is especially important for single-variant products
        if (!this.cart) {
          this.cart = document.querySelector('cart-notification') ||
                     document.querySelector('cart-drawer');
        }
        
        // Debug: log if cart drawer is missing
        if (!this.cart) {
          console.warn('Cart drawer not found! Will redirect to cart page.');
        } else {
          console.log('Cart drawer found, will open drawer');
        }

        if (!this.submitButton || !this.submitButtonText) {
          console.error('Submit button or button text not found');
          return;
        }

        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        this.submitButton.disabled = false; // Keep enabled so spinner shows properly
        
        // Temporarily disable button watcher for single-variant products during loading
        const isSingleVariant = this.hasAttribute('data-single-variant');
        if (isSingleVariant) {
          const productInfo = this.closest('product-info');
          if (productInfo && productInfo.buttonWatcher) {
            productInfo.buttonWatcher.disconnect();
            productInfo.isLoading = true; // Mark as loading to prevent re-enabling watcher
          }
        }

        // Store original text if not already stored
        if (!this.originalButtonText) {
          this.originalButtonText = this.submitButtonText.innerHTML;
        }

        // Show loading spinner - replace button text
        const spinnerHTML = '<div class="lds-ring button-spinner-white"><span></span><span></span><span></span><span></span></div>';
        this.submitButtonText.innerHTML = spinnerHTML;
        this.submitButtonText.style.color = '#ffffff';

        // Also show the existing spinner element if it exists
        const existingSpinner = this.querySelector('.loading__spinner');
        if (existingSpinner) {
          existingSpinner.classList.remove('hidden');
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        // Always use the most current variant ID from the input, not form data which might be stale
        // This ensures we use the latest variant selection even if FormData hasn't updated
        if (variantId) {
          formData.set('id', variantId);
        } else {
          // Fallback: try to get from form data, but log warning
          const formDataVariantId = formData.get('id');
          if (!formDataVariantId || formDataVariantId.trim() === '') {
            console.warn('Variant ID not found in form');
            this.handleErrorMessage('Please select a size');
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-disabled');
            return;
          }
        }
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.section)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json();
          })
          .then((response) => {
            // Check for error status
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description || response.message);

              const soldOutMessage =
                this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              // Last resort: try to find cart drawer one more time before redirecting
              const cartDrawer = document.querySelector('cart-notification') ||
                               document.querySelector('cart-drawer');
              if (cartDrawer) {
                this.cart = cartDrawer;
                // Re-render with the found cart drawer
                this.cart.renderContents(response);
                return;
              }
              // Only redirect if cart drawer truly doesn't exist
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            
            // Render cart drawer contents - this will open the drawer
            this.cart.renderContents(response);
            
            // Wait for cart drawer to fully open before removing loading state
            // Poll for drawer visibility instead of relying on transitionend (more reliable)
            let checkCount = 0;
            const maxChecks = 60; // 3 seconds max (60 * 50ms)
            
            const checkDrawerVisible = () => {
              checkCount++;
              const isVisible = this.cart && this.cart.classList.contains('is-visible');
              
              if (isVisible) {
                // Drawer is visible - wait for CSS transition to complete (usually 300-400ms)
                setTimeout(() => {
                  // Only remove if still loading
                  if (this.submitButton && this.submitButton.classList.contains('loading')) {
                    this.removeLoadingState();
                  }
                }, 500); // Wait for transition animation
              } else {
                // Drawer not visible yet - check again
                if (checkCount < maxChecks) {
                  setTimeout(checkDrawerVisible, 50);
                } else {
                  // Timeout - remove loading state anyway
                  if (this.submitButton && this.submitButton.classList.contains('loading')) {
                    this.removeLoadingState();
                  }
                }
              }
            };
            
            // Start checking after a short delay to allow renderContents to execute
            setTimeout(checkDrawerVisible, 100);
          })
          .catch((e) => {
            console.error('Add to cart error:', e);
            this.handleErrorMessage(
              'Failed to add product to cart. Please try again.'
            );
            this.error = true;
            this.removeLoadingState();
          });
      }
      
      removeLoadingState() {
        if (!this.submitButton || !this.submitButtonText) return;
        
        this.submitButton.classList.remove('loading');
        this.submitButton.disabled = false;
        
        // Re-enable button watcher for single-variant products after loading
        const isSingleVariant = this.hasAttribute('data-single-variant');
        if (isSingleVariant) {
          const productInfo = this.closest('product-info');
          if (productInfo && productInfo.isLoading) {
            productInfo.isLoading = false;
            // Re-setup button watcher if needed
            if (productInfo.setupButtonWatcher && typeof productInfo.setupButtonWatcher === 'function') {
              productInfo.setupButtonWatcher();
            }
          }
        }
        
        if (this.cart && this.cart.classList.contains('is-empty'))
          this.cart.classList.remove('is-empty');

        // Only remove aria-disabled if there's no error
        if (!this.error) {
          this.submitButton.removeAttribute('aria-disabled');
          // Restore original button text
          if (this.originalButtonText && this.submitButtonText) {
            this.submitButtonText.innerHTML = this.originalButtonText;
            this.submitButtonText.style.color = '';
          }
        } else {
          // On error, keep button disabled but restore text
          if (this.originalButtonText && this.submitButtonText) {
            this.submitButtonText.innerHTML = this.originalButtonText;
            this.submitButtonText.style.color = '';
          }
        }
        
        // Hide the spinner element
        const existingSpinner = this.querySelector('.loading__spinner');
        if (existingSpinner) {
          existingSpinner.classList.add('hidden');
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        // Check if this is a single-variant product - COMPLETELY IGNORE all calls for single-variant products
        const isSingleVariant = this.hasAttribute('data-single-variant') || 
                               this.dataset.singleVariant === 'true';
        
        if (isSingleVariant) {
          // For single-variant products, NEVER allow JavaScript to disable or change the button
          // The button state is controlled entirely by Liquid - JavaScript should not touch it
          const variantInput = this.querySelector('input[name="id"]');
          const submitButton = this.submitButton;
          
          if (!variantInput || !variantInput.value) {
            // No variant - this is an error state, allow disabled
            submitButton.setAttribute('disabled', 'disabled');
            submitButton.setAttribute('aria-disabled', 'true');
            if (text && text !== 'SELECT A SIZE') {
              this.submitButtonText.textContent = text;
            }
            return;
          }
          
          // Check if variant is available (not disabled in input)
          const isVariantAvailable = !variantInput.hasAttribute('disabled');
          
          if (isVariantAvailable) {
            // Variant is available - FORCE button to be enabled with correct text
            // Ignore any disable attempts
            submitButton.removeAttribute('disabled');
            submitButton.removeAttribute('aria-disabled');
            this.submitButtonText.textContent = this.hasAttribute('data-has-pre-order')
              ? window.variantStrings?.preOrder || 'Pre-order'
              : window.variantStrings?.addToCart || 'Add to cart';
          } else {
            // Variant is sold out - only allow sold out state
            submitButton.setAttribute('disabled', 'disabled');
            submitButton.setAttribute('aria-disabled', 'true');
            this.submitButtonText.textContent = window.variantStrings?.soldOut || 'Sold Out';
          }
          
          // NEVER allow "SELECT A SIZE" text for single-variant products
          if (text === 'SELECT A SIZE') {
            // Ignore this completely - don't change anything
            return;
          }
          
          return; // Exit early - don't run normal logic
        }
        
        // Regular logic for multi-variant products
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButtonText.textContent = this.hasAttribute(
            'data-has-selling-plan'
          )
            ? window.variantStrings.addSubscriptionToCart
            : (this.submitButtonText.textContent = this.hasAttribute(
                'data-has-pre-order'
              )
                ? window.variantStrings.preOrder
                : window.variantStrings.addToCart);
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      // Ensure submitButton is always accessible (for Swym and other scripts)
      get submitButton() {
        if (!this._submitButton || !document.contains(this._submitButton)) {
          this._submitButton = this.querySelector('[type="submit"]');
        }
        return this._submitButton;
      }

      // Ensure submitButtonText is always accessible
      get submitButtonText() {
        if (!this._submitButtonText || !document.contains(this._submitButtonText)) {
          this._submitButtonText = this.submitButton?.querySelector('span');
        }
        return this._submitButtonText;
      }
    }
  );
}

document.querySelectorAll('.product__content > *').forEach((el) => {
  if (!el.textContent.trim()) {
    el.textContent = '';
  }
});
